import setGlobalState from '../../../redux/setGlobalState';
import store from '../../../store';
import getTicketsPagePath from '../../../library/getTicketsPagePath';
import {getEventTickets} from '../../../redux/tickets/ticketsActions';
import {getPlans, setCurrentPlan} from '../../../redux/subscription/subscriptionActions';
import type {AiAgentExecuteResult, AiAgentIntent} from '../../../redux/aiAgent/aiAgent.type';
import {resetAgentEventState} from './resetAgentEventState';

export type NavigateFn = (to: string | number, options?: {state?: unknown}) => void;

type AfterExecuteOpener = (
    result: AiAgentExecuteResult,
    navigate: NavigateFn,
) => void | Promise<void>;

/**
 * After execute, open that record's editor for review.
 */
function openEventWizard(
    {eid, eventType, reviewRoute}: AiAgentExecuteResult,
    navigate: NavigateFn,
): void {
    // The wizard picks its steps from ui.event flags, which changeEvent never clears — left
    // behind, the previous event's flags choose the steps of this one.
    resetAgentEventState();
    if(eventType === 'ticket' || eventType === 'area') {
        setGlobalState('ui.show.isOpenTickchakLoader', true);
    }
    setGlobalState('ui.show.isOpenCreateEvent', false);
    if(eventType === 'scheduled') {
        setGlobalState('ui.event.isScheduled', true);
    }
    // AppComponent changeEvent resets slices + loads getEvent/permissions.
    navigate(reviewRoute || `/e/${eid}/event`);
}

async function openTicketPopup(
    {eid, eventType, tid, reviewRoute}: AiAgentExecuteResult,
    navigate: NavigateFn,
): Promise<void> {
    if(!tid) {
        throw new Error('aiAgent: execute returned CREATE_TICKET without a tid');
    }
    if(eventType === 'subscription') {
        await getPlans();
        setCurrentPlan(tid);
        setGlobalState('ui.show.isOpenCreateSubscription', true);
        navigate(reviewRoute || `/e/${eid}/subscription`);
        return;
    }
    // The page opens the popup on the created ticket, so its list has to hold it first.
    await getEventTickets();
    setGlobalState('aiAgent.pendingTicketTid', tid);
    navigate(reviewRoute || `/e/${eid}/${getTicketsPagePath(store.getState().event)}`);
}

/**
 * One opener per intent — a missing key is a compile error when AiAgentIntent grows,
 * instead of a draft that exists and a form that never opens.
 */
const AFTER_EXECUTE_OPENERS: Record<AiAgentIntent, AfterExecuteOpener> = {
    CREATE_EVENT: openEventWizard,
    CREATE_TICKET: openTicketPopup,
};

export async function navigateAfterExecute(
    result: AiAgentExecuteResult,
    navigate: NavigateFn,
): Promise<void> {
    const openReviewForm = AFTER_EXECUTE_OPENERS[result.intent];
    // The caller turns this into "created but not opened" — an intent this client has no
    // opener for is a failure to open, not a lost record.
    if(!openReviewForm) {
        throw new Error(`aiAgent: no after-execute opener for intent ${result.intent}`);
    }
    await openReviewForm(result, navigate);
    setGlobalState('aiAgent.isOpen', false);
}
